import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { AID_STATUSES, AID_STATUS_STYLE, aidActionConflict, confirmBlocker } from "@/lib/billingAidStatus";

// The appearance's legal aid status, as the Aid Status Diary used to hold it: filled from
// the Aid Planner, checked and confirmed here. LACW see it in their portal.
export default function AidStatusStrip({ entryId, aid, suggestion, action, onAidChange, onUseSuggestion }) {
  const set = (patch) => onAidChange?.(entryId, patch);
  const conflict = aidActionConflict(aid.aidStatus, action);
  const filledFromPlanner = aid.aidPlannerFill && aid.aidStatus === suggestion?.status;
  const blocker = aid.confirmed ? "" : confirmBlocker(aid);

  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`aid-status-${entryId}`} className="text-xs font-bold uppercase tracking-wide text-slate-400">Aid</label>
        <select
          id={`aid-status-${entryId}`}
          value={aid.aidStatus}
          onChange={e => set({ aidStatus: e.target.value, confirmed: false, confirmedBy: "", confirmedAt: "", grantActionLodged: false })}
          className={`rounded-lg border px-2 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-300 ${AID_STATUS_STYLE[aid.aidStatus] || AID_STATUS_STYLE["Not checked"]}`}
        >
          {AID_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
        <input
          aria-label="Aid number"
          value={aid.aidNumber || ""}
          onChange={e => set(aid.confirmed && aid.aidStatus === "Aid confirmed" && !e.target.value.trim()
            ? { aidNumber: e.target.value, confirmed: false, confirmedAt: "" }
            : { aidNumber: e.target.value })}
          placeholder="Aid number"
          className="w-28 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-purple-300"
        />
        <label title={blocker} className={`inline-flex items-center gap-1.5 text-xs font-semibold ${blocker ? "cursor-not-allowed text-slate-400" : "cursor-pointer text-slate-700"}`}>
          <input
            type="checkbox"
            disabled={Boolean(blocker)}
            checked={Boolean(aid.confirmed)}
            onChange={e => set(e.target.checked
              ? { confirmed: true, confirmedAt: new Date().toISOString() }
              : { confirmed: false, confirmedBy: "", confirmedAt: "" })}
            className="h-3.5 w-3.5 accent-emerald-600"
          />
          {aid.confirmed ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Confirmed</span> : "Confirm"}
        </label>
        {aid.grantActionLodged && <span className="text-xs font-semibold text-emerald-700">Application marked as lodged</span>}
      </div>
      {suggestion?.ambiguous ? (
        <p className="mt-1 text-[11px] font-semibold text-amber-800">{suggestion.ambiguous} Aid Planner matters have this name. Check which one.</p>
      ) : filledFromPlanner ? (
        <p title={suggestion.reason} className="mt-1 text-[11px] font-semibold text-violet-800">Filled from the Aid Planner ({suggestion.matterName}). Check and confirm.</p>
      ) : suggestion?.status && suggestion.status !== aid.aidStatus ? (
        <p className="mt-1 text-[11px] text-slate-600">
          Aid Planner says: <strong>{suggestion.status}</strong>{suggestion.aidNumber && suggestion.status === "Aid confirmed" ? ` (${suggestion.aidNumber})` : ""}.{" "}
          <button type="button" onClick={() => onUseSuggestion?.(entryId, suggestion)} className="font-bold text-violet-700 hover:underline">Use this</button>
        </p>
      ) : null}
      {conflict && (
        <p className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {conflict}
        </p>
      )}
    </div>
  );
}
